
import React, { useState, useEffect } from 'react';
import {
  TechniqueFilters as FiltersType,
  TechniquePosition,
  TechniqueType,
  TechniqueDifficulty,
  Position
} from '@/types/technique';
import { SearchBar } from './filters/SearchBar';
import { FilterToggle } from './filters/FilterToggle';
import { FilterOptions } from './filters/FilterOptions';

interface TechniqueFiltersProps {
  positions: Position[];
  onFilterChange: (filters: FiltersType) => void;
  currentFilters: FiltersType;
  onClearFilters: () => void;
}

export const TechniqueFilters = ({
  positions,
  onFilterChange,
  currentFilters,
  onClearFilters
}: TechniqueFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(currentFilters.search || '');

  // Make sure we sync with parent component when currentFilters change
  useEffect(() => {
    const newSearchValue = currentFilters.search || '';
    if (newSearchValue !== search) {
      setSearch(newSearchValue);
    }
  }, [currentFilters.search, search]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Apply the search filter immediately
    onFilterChange({ ...currentFilters, search: value });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({ ...currentFilters, search });
  };

  const handlePositionChange = (value: string) => {
    console.log("Position changed to:", value);
    onFilterChange({ ...currentFilters, position: value === "all" ? undefined : value as TechniquePosition });
  };

  const handleTechniquePositionChange = (value: string) => {
    console.log("Technique position changed to:", value);
    onFilterChange({
      ...currentFilters,
      technique_position: value === "all" ? undefined : (value as TechniquePosition)
    });
  };

  const handleTechniqueTypeChange = (value: string) => {
    console.log("Technique type changed to:", value);
    onFilterChange({
      ...currentFilters,
      technique_type: value === "all" ? undefined : (value as TechniqueType)
    });
  };

  const handleDifficultyChange = (value: string) => {
    onFilterChange({
      ...currentFilters,
      difficulty: value === "all" ? undefined : (value as TechniqueDifficulty)
    });
  };

  const handleExcludeUserCreatedChange = (value: boolean) => {
    onFilterChange({
      ...currentFilters,
      exclude_user_created: value
    });
  };

  const clearFilters = (e: React.MouseEvent) => {
    e.preventDefault(); 
    e.stopPropagation();
    setSearch('');
    onClearFilters();
  };

  const hasActiveFilters = Object.values(currentFilters).some(value => value !== undefined);
  const filtersCount = Object.values(currentFilters).filter(Boolean).length;

  return (
    <div className="space-y-4 bg-card rounded-lg p-4 border">
      <SearchBar 
        value={search}
        onChange={handleSearchChange}
        onSubmit={handleSearchSubmit}
      />
      
      <FilterToggle
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        hasActiveFilters={hasActiveFilters}
        filtersCount={filtersCount}
        onClearFilters={clearFilters}
      >
        <FilterOptions
          positions={positions}
          selectedPosition={currentFilters.position}
          selectedTechniquePosition={currentFilters.technique_position}
          selectedTechniqueType={currentFilters.technique_type}
          selectedDifficulty={currentFilters.difficulty}
          excludeUserCreated={currentFilters.exclude_user_created}
          onPositionChange={handlePositionChange}
          onTechniquePositionChange={handleTechniquePositionChange}
          onTechniqueTypeChange={handleTechniqueTypeChange}
          onDifficultyChange={handleDifficultyChange}
          onExcludeUserCreatedChange={handleExcludeUserCreatedChange}
        />
      </FilterToggle>
    </div>
  );
};
